import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multiselect';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { SlidersHorizontal } from 'lucide-react';

export interface ExpenseFilters {
  statuses?: string[];        // 'approved' | 'pending'
  employees?: string[];       // employeeId as string
  types?: string[];           // expense.category values (Type column)
  categories?: string[];      // expense.paymentMethod values (Category column)
  costCentres?: string[];     // expense.costCentreId values
  receiptAttached?: string[]; // 'attached' | 'none'
  vatCodes?: string[];        // expense.taxCodeId values
  dateFrom?: string;
  dateTo?: string;
  amountMin?: string;
  amountMax?: string;
}

interface ExpenseEntriesFilterSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: ExpenseFilters;
  onApply: (filters: ExpenseFilters) => void;
  onClear: () => void;
  availableEmployees: Array<{ value: string; label: string }>;
  availableTypes: Array<{ value: string; label: string }>;
  availableCategories: Array<{ value: string; label: string }>;
  availableVatCodes: Array<{ value: string; label: string }>;
  availableCostCentres: Array<{ value: string; label: string }>;
}

export function countActiveExpenseFilters(filters: ExpenseFilters): number {
  let count = 0;
  if (filters.statuses?.length)        count++;
  if (filters.employees?.length)       count++;
  if (filters.types?.length)           count++;
  if (filters.categories?.length)      count++;
  if (filters.costCentres?.length)     count++;
  if (filters.receiptAttached?.length) count++;
  if (filters.vatCodes?.length)        count++;
  if (filters.dateFrom || filters.dateTo) count++;
  if (filters.amountMin || filters.amountMax) count++;
  return count;
}

export function applyExpenseFilters<T extends {
  approved: boolean;
  employeeId: number;
  category: string;
  date: string;
  amount: number;
  paymentMethod?: string;
  taxCodeId?: string;
  costCentreId?: string;
  receiptUrl?: string;
  attachments?: unknown[];
}>(items: T[], filters: ExpenseFilters): T[] {
  return items.filter((item) => {
    // Status
    if (filters.statuses?.length) {
      const status = item.approved ? 'approved' : 'pending';
      if (!filters.statuses.includes(status)) return false;
    }
    // Employee
    if (filters.employees?.length) {
      if (!filters.employees.includes(String(item.employeeId))) return false;
    }
    // Type (expense.category)
    if (filters.types?.length) {
      if (!filters.types.includes(item.category)) return false;
    }
    // Category (expense.paymentMethod)
    if (filters.categories?.length) {
      if (!item.paymentMethod || !filters.categories.includes(item.paymentMethod)) return false;
    }
    // Cost Centre
    if (filters.costCentres?.length) {
      if (!item.costCentreId || !filters.costCentres.includes(item.costCentreId)) return false;
    }
    // Receipt
    if (filters.receiptAttached?.length) {
      const hasReceipt = !!(item.receiptUrl || (item.attachments && item.attachments.length > 0));
      const receiptStatus = hasReceipt ? 'attached' : 'none';
      if (!filters.receiptAttached.includes(receiptStatus)) return false;
    }
    // VAT Code
    if (filters.vatCodes?.length) {
      if (!item.taxCodeId || !filters.vatCodes.includes(item.taxCodeId)) return false;
    }
    // Date range
    if (filters.dateFrom && item.date < filters.dateFrom) return false;
    if (filters.dateTo && item.date > filters.dateTo) return false;
    // Amount range
    if (filters.amountMin && item.amount < Number(filters.amountMin)) return false;
    if (filters.amountMax && item.amount > Number(filters.amountMax)) return false;

    return true;
  });
}

const STATUS_OPTIONS = [
  { value: 'approved', label: 'Approved' },
  { value: 'pending',  label: 'Pending' },
];

const RECEIPT_OPTIONS = [
  { value: 'attached', label: 'Receipt Attached' },
  { value: 'none',     label: 'No Receipt' },
];

/** Maximum absolute value for numeric filter inputs (prevents scientific notation overflow) */
const NUMERIC_FILTER_MAX_VALUE = 999_999_999_999;

export function ExpenseEntriesFilterSheet({
  open,
  onOpenChange,
  filters,
  onApply,
  onClear,
  availableEmployees,
  availableTypes,
  availableCategories,
  availableVatCodes,
  availableCostCentres,
}: ExpenseEntriesFilterSheetProps) {
  const [draft, setDraft] = useState<ExpenseFilters>(filters);

  useEffect(() => {
    if (open) setDraft(filters);
  }, [open, filters]);

  const set = <K extends keyof ExpenseFilters>(key: K, value: ExpenseFilters[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const handleApply = () => {
    onApply(draft);
    onOpenChange(false);
  };

  const handleClear = () => {
    setDraft({});
    onClear();
    onOpenChange(false);
  };

  const activeCount = countActiveExpenseFilters(draft);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        style={{ maxWidth: 520, width: '100%' }}
        className="sm:max-w-[560px] flex flex-col p-0"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="h-5 w-5 text-muted-foreground" />
            <SheetTitle>Advanced Filters</SheetTitle>
            {activeCount > 0 && (
              <Badge variant="default" className="ml-auto text-xs">
                {activeCount} active
              </Badge>
            )}
          </div>
          <SheetDescription>
            Narrow down expenses using the filters below.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">

          {/* Date Range */}
          <div className="space-y-2">
            <Label>Date Range</Label>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={draft.dateFrom ?? ''}
                onChange={(e) => set('dateFrom', e.target.value || undefined)}
                className="flex-1"
              />
              <span className="text-muted-foreground text-sm shrink-0">to</span>
              <Input
                type="date"
                value={draft.dateTo ?? ''}
                onChange={(e) => set('dateTo', e.target.value || undefined)}
                className="flex-1"
              />
            </div>
          </div>

          {/* Employee */}
          <div className="space-y-2">
            <Label>Employee</Label>
            <MultiSelect
              value={draft.employees ?? []}
              onValueChange={(v) => set('employees', v)}
              options={availableEmployees}
              placeholder="All employees"
              emptyMessage="No employees found."
              maxDisplay={3}
            />
          </div>

          {/* Type (expense.category) */}
          <div className="space-y-2">
            <Label>Type</Label>
            <MultiSelect
              value={draft.types ?? []}
              onValueChange={(v) => set('types', v)}
              options={availableTypes}
              placeholder="All types"
              emptyMessage="No types found."
              maxDisplay={3}
            />
          </div>

          {/* Category (expense.paymentMethod) */}
          <div className="space-y-2">
            <Label>Category</Label>
            <MultiSelect
              value={draft.categories ?? []}
              onValueChange={(v) => set('categories', v)}
              options={availableCategories}
              placeholder="All categories"
              emptyMessage="No categories found."
              maxDisplay={3}
            />
          </div>

          {/* Cost Centre */}
          {availableCostCentres.length > 0 && (
            <div className="space-y-2">
              <Label>Cost Centre</Label>
              <MultiSelect
                value={draft.costCentres ?? []}
                onValueChange={(v) => set('costCentres', v)}
                options={availableCostCentres}
                placeholder="All cost centres"
                emptyMessage="No cost centres found."
                maxDisplay={3}
              />
            </div>
          )}

          {/* Approval Status */}
          <div className="space-y-2">
            <Label>Status</Label>
            <MultiSelect
              value={draft.statuses ?? []}
              onValueChange={(v) => set('statuses', v)}
              options={STATUS_OPTIONS}
              placeholder="All statuses"
              emptyMessage="No statuses found."
            />
          </div>

          {/* Amount Range */}
          <div className="space-y-2">
            <Label>Amount Range</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={0}
                step={0.01}
                placeholder="Min"
                value={draft.amountMin ?? ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw !== '' && (!isFinite(Number(raw)) || Math.abs(Number(raw)) > NUMERIC_FILTER_MAX_VALUE)) return;
                  set('amountMin', raw || undefined);
                }}
                className="flex-1"
              />
              <span className="text-muted-foreground text-sm shrink-0">–</span>
              <Input
                type="number"
                min={0}
                step={0.01}
                placeholder="Max"
                value={draft.amountMax ?? ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw !== '' && (!isFinite(Number(raw)) || Math.abs(Number(raw)) > NUMERIC_FILTER_MAX_VALUE)) return;
                  set('amountMax', raw || undefined);
                }}
                className="flex-1"
              />
            </div>
          </div>

          {/* VAT Code */}
          {availableVatCodes.length > 0 && (
            <div className="space-y-2">
              <Label>VAT Code</Label>
              <MultiSelect
                value={draft.vatCodes ?? []}
                onValueChange={(v) => set('vatCodes', v)}
                options={availableVatCodes}
                placeholder="All VAT codes"
                emptyMessage="No VAT codes found."
                maxDisplay={3}
              />
            </div>
          )}

          {/* Receipt */}
          <div className="space-y-2">
            <Label>Receipt</Label>
            <MultiSelect
              value={draft.receiptAttached ?? []}
              onValueChange={(v) => set('receiptAttached', v)}
              options={RECEIPT_OPTIONS}
              placeholder="All"
              emptyMessage="No options found."
            />
          </div>

        </div>

        <SheetFooter className="px-6 py-4 border-t flex flex-row gap-2">
          <Button variant="outline" onClick={handleClear} className="flex-1">
            Clear All
          </Button>
          <Button onClick={handleApply} className="flex-1">
            Apply Filters{activeCount > 0 ? ` (${activeCount})` : ''}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
